// src/components/Chatbot.jsx
import React, { useState, useRef } from "react";
import { auth } from "../firebase";
import { getAllEvents } from "../services/eventsService";

const OPENAI_URL = "https://api.openai.com/v1/chat/completions";
const OPENAI_MODEL = "gpt-4o-mini"; // change if needed

// ---------- Helpers for events & dates ----------

function toJsDate(startAt) {
  if (!startAt) return null;
  if (startAt.toDate) return startAt.toDate(); // Firestore Timestamp
  if (typeof startAt.seconds === "number") {
    return new Date(startAt.seconds * 1000);
  }
  return new Date(startAt);
}

function formatDateTime(startAt) {
  const d = toJsDate(startAt);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------- System-aware answer (service times, location, events) ----------

async function getSystemAnswer(rawQuestion) {
  if (!rawQuestion) return null;
  const q = rawQuestion.toLowerCase().trim();

  const asksAboutServiceTime =
    q.includes("service time") ||
    q.includes("what time is church") ||
    q.includes("what time is the service") ||
    q.includes("sunday service") ||
    q.includes("church time");

  const asksAboutLocation =
    q.includes("where is the church") ||
    q.includes("church location") ||
    q.includes("where are you") ||
    q.includes("address") ||
    q.includes("location");

  const asksAboutEvents =
    q.includes("events") ||
    q.includes("what's happening") ||
    q.includes("whats happening") ||
    q.includes("what is happening") ||
    q.includes("any event") ||
    q.includes("happening at church") ||
    q.includes("upcoming event");

  // 1) Location – simple static answer
  if (asksAboutLocation) {
    // 👉 EDIT THIS FOR YOUR DEMO
    return `
Our church currently meets at:

Smart Church CMS Demo Campus
123 Example Street, Pretoria, South Africa

You can also see the exact location on each event card under the Events section.
    `.trim();
  }

  // 2) Service times / events – read from Firestore via eventsService
  if (asksAboutServiceTime || asksAboutEvents) {
    let events = [];
    try {
      const data = await getAllEvents();
      events = Array.isArray(data) ? data : [];
    } catch (err) {
      console.error("Failed to load events in chatbot:", err);
      // If we were only asking about generic events, fall back to AI
      if (!asksAboutServiceTime) return null;
    }

    const now = new Date();

    const sundayEvents = events
      .filter((ev) => ev.isSundayService)
      .map((ev) => ({ ev, date: toJsDate(ev.startAt) }))
      .filter((x) => x.date)
      .sort((a, b) => a.date - b.date);

    const otherEvents = events
      .filter((ev) => !ev.isSundayService)
      .map((ev) => ({ ev, date: toJsDate(ev.startAt) }))
      .filter((x) => x.date && x.date >= now)
      .sort((a, b) => a.date - b.date);

    // 2a) Specifically about service time
    if (asksAboutServiceTime) {
      if (sundayEvents.length === 0) {
        return `
We don't have a Sunday Service event configured in the system yet.

For the demo, you can add one under:
Admin → Manage Events → create a "Sunday Service" event with the correct time.
        `.trim();
      }

      const nextSunday = sundayEvents.find((x) => x.date >= now) || sundayEvents[0];
      const d = nextSunday.date;
      const timeStr = d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
      const location = nextSunday.ev.location || "our usual church venue";

      return `
According to the system, the next Sunday Service is:

📅 ${d.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      })}
⏰ Starts at: ${timeStr}
📍 Location: ${location}

You can also open the Events page and check in on Sunday.
      `.trim();
    }

    // 2b) Generally about events
    if (asksAboutEvents) {
      if (events.length === 0) {
        return "There are currently no events in the system. Please check back later.";
      }

      const upcoming = [...sundayEvents, ...otherEvents]
        .filter((x) => x.date && x.date >= now)
        .slice(0, 5);

      if (upcoming.length === 0) {
        return "There are no upcoming events scheduled in the system right now.";
      }

      const listLines = upcoming.map(({ ev }) => {
        const title = ev.title || (ev.isSundayService ? "Sunday Service" : "Event");
        const dateStr = formatDateTime(ev.startAt);
        const location = ev.location ? ` • 📍 ${ev.location}` : "";
        return `• ${title} – ${dateStr}${location}`;
      });

      return `
Here are the next events from the system:

${listLines.join("\n")}

You can see more details and RSVP from the Events page.
      `.trim();
    }
  }

  // ❌ If we don't recognise it as a system question, return null so OpenAI can answer
  return null;
}

export default function Chatbot() {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]); // {role, text}
  const [loading, setLoading] = useState(false);
  const lastCallRef = useRef(0);

  async function send() {
    const q = (question || "").trim();
    if (!q) return;

    // simple rate limit: 1 request every 5 seconds
    const now = Date.now();
    if (now - lastCallRef.current < 5000) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "Please wait a couple seconds before sending another question.",
        },
      ]);
      return;
    }
    lastCallRef.current = now;

    // Add user message to chat
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setQuestion("");
    setLoading(true);

    try {
      // 1️⃣ Try answer from system (events, location, Sunday service)
      const systemAnswer = await getSystemAnswer(q);
      if (systemAnswer) {
        setMessages((prev) => [...prev, { role: "bot", text: systemAnswer }]);
        return;
      }

      // 2️⃣ Fallback to OpenAI, like before
      const userObj = auth.currentUser;
      const userLabel = userObj
        ? userObj.displayName || userObj.email
        : "Guest";

      const messagesPayload = [
        {
          role: "system",
          content:
            "You are a helpful, pastoral church assistant. Be concise and kind. If the user asks about this church's events/service times/location, but no system data was used, answer in a general way.",
        },
        {
          role: "user",
          content: `User: ${userLabel}\nQuestion: ${q}\n\nAnswer succinctly and give practical steps or references.`,
        },
      ];

      const resp = await fetch(OPENAI_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_KEY}`,
        },
        body: JSON.stringify({
          model: OPENAI_MODEL,
          messages: messagesPayload,
          max_tokens: 400,
          temperature: 0.6,
        }),
      });

      if (!resp.ok) {
        const errBody = await resp.text().catch(() => "");
        throw new Error(`OpenAI error ${resp.status}: ${errBody}`);
      }

      const data = await resp.json();
      let assistantText = "";
      try {
        if (
          data &&
          data.choices &&
          data.choices.length > 0 &&
          data.choices[0].message &&
          data.choices[0].message.content
        ) {
          assistantText = String(data.choices[0].message.content).trim();
        }
      } catch (e) {
        assistantText = "";
      }

      if (!assistantText)
        assistantText = "Sorry — I couldn't produce an answer. Try again.";

      setMessages((prev) => [...prev, { role: "bot", text: assistantText }]);
    } catch (err) {
      console.error("Chat error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "An error occurred while calling the AI. See console.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-xl font-semibold mb-3">Ask the Church Assistant</h2>

      <div className="border rounded p-3 mb-3 h-64 overflow-auto bg-white">
        {messages.length === 0 && (
          <div className="text-sm text-gray-500">
            Ask about service times, locations, upcoming events, or any faith
            question…
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`mb-2 ${
              m.role === "user" ? "text-right" : "text-left"
            }`}
          >
            <div
              className={`inline-block p-2 rounded ${
                m.role === "user" ? "bg-indigo-100" : "bg-gray-100"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          className="flex-1 p-2 border rounded"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Type your question..."
          onKeyDown={(e) => {
            if (e.key === "Enter") send();
          }}
        />
        <button
          onClick={send}
          disabled={loading}
          className="px-4 py-2 bg-indigo-600 text-white rounded"
        >
          {loading ? "Thinking…" : "Send"}
        </button>
      </div>
    </div>
  );
}
